import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetCallFlowQuery,
  useUpdateCallFlowMutation,
  useGetOwnPhoneNumbersQuery,
} from "@/store/rest_api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { CallFlowType } from "@/model/interface/CallFlowType";
import { buildCanvasFromFlow } from "@/util/flow";
import { apiErrorMessage } from "@/util/apiError";
import CallFlowEditor, { CallFlowPersistPayload, EditorTab } from "./components/CallFlowEditor";
import { useCollapsedSidebar } from "@/hooks/useCollapsedSidebar";

export default function EditCallFlow() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: callFlow, isLoading: isLoadingFlow, refetch } = useGetCallFlowQuery(id!);
  const [updateCallFlow, { isLoading: isSaving }] = useUpdateCallFlowMutation();
  const { isLoading: isLoadingPhoneNumbers } = useGetOwnPhoneNumbersQuery();
  const [editorVersion, setEditorVersion] = useState(0);
  const [activeTab, setActiveTab] = useState<EditorTab>("details");
  useCollapsedSidebar(activeTab === "flow");

  const canvas = useMemo(() => (callFlow ? buildCanvasFromFlow(callFlow) : { nodes: [], edges: [] }), [callFlow]);

  const reloadAfterRestore = async () => {
    await refetch();
    setEditorVersion((version) => version + 1);
  };

  const persistFlow = async ({ values, active, nodes, edges, actions }: CallFlowPersistPayload) => {
    try {
      await updateCallFlow({
        id: id!,
        data: {
          name: values.name,
          description: values.description,
          type: values.type as CallFlowType,
          phoneNumber:
            values.phoneNumberId && values.phoneNumberId !== "-" ? { id: values.phoneNumberId } : undefined,
          flowData: {
            nodes,
            edges,
          },
          actions,
          active,
          callerIdLookupMode: values.callerIdLookupMode,
          ringbackAudioClipId: values.ringbackAudioClipId ?? null,
          holdAudioClipId: values.holdAudioClipId ?? null,
          screenUnknownCallers: values.screenUnknownCallers,
        },
      }).unwrap();

      toast.success("Call flow updated successfully");
      return true;
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to update call flow"));
      return false;
    }
  };

  if (isLoadingFlow || !callFlow || isLoadingPhoneNumbers) {
    return (
      <div className="space-y-6 p-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate("/manage-organisation/telephony?tab=call-flows")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Edit Call Flow</h1>
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
        <Card>
          <CardContent className="py-12">
            <div className="space-y-4 animate-pulse">
              <div className="h-4 bg-muted rounded w-1/3" />
              <div className="h-10 bg-muted rounded" />
              <div className="h-4 bg-muted rounded w-1/4" />
              <div className="h-20 bg-muted rounded" />
              <div className="h-4 bg-muted rounded w-1/4" />
              <div className="h-10 bg-muted rounded" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <CallFlowEditor
      key={editorVersion}
      title="Edit Call Flow"
      subtitle="Modify your call routing configuration"
      submitLabel="Save Changes"
      isSaving={isSaving}
      initialValues={{
        name: callFlow.name || "",
        description: callFlow.description || "",
        type: callFlow.type || CallFlowType.BASIC,
        phoneNumberId: callFlow.phoneNumber?.id || "",
        callerIdLookupMode: callFlow.callerIdLookupMode ?? "OFF",
        ringbackAudioClipId: callFlow.ringbackAudioClipId ?? null,
        holdAudioClipId: callFlow.holdAudioClipId ?? null,
        screenUnknownCallers: callFlow.screenUnknownCallers ?? false,
        active: callFlow.active ?? true,
      }}
      initialNodes={canvas.nodes}
      initialEdges={canvas.edges}
      currentFlowId={id}
      initialTab={activeTab}
      onActiveTabChange={setActiveTab}
      onPersist={persistFlow}
      onRestored={reloadAfterRestore}
    />
  );
}
